import { AnalysisStatus } from '@/features/workspaces/constants';
import type { KnowledgeBase } from '@/features/knowledge-base/types';
import type {
  DeviationsFlatRow,
  FlatStatement,
  StandardDeviation,
  StandardDeviationTemplateStatus,
  StandardDeviationTemplateStatusData,
} from '@/features/standard-deviations/types';
import { UserReview } from '@/features/user-review/constants';
import {
  getUserReviewSortValue,
  isIgnoredUserReview,
  normalizeUserReview,
} from '@/features/user-review/utils';
import type { StandardDeviationsFilter } from '@/features/standard-deviations/filters';
import type { StandardDeviationStatementType } from '@/features/standard-deviations/constants';
import { SubjectKind } from '@/subjects';
import { failsModeFilter } from '@/filters';

export const flattenStandardDeviationStatements = (
  items?: StandardDeviation[],
): FlatStatement[] => {
  if (!items || items.length === 0) return [];
  const out: FlatStatement[] = [];
  for (const sd of items) {
    const sid = sd.standardEvalId;
    for (const st of sd.statements || []) out.push({ ...st, standardEvalId: sid });
  }
  return out;
};

export type FindingSeverityLevel = 'high' | 'medium' | 'low';

export const getFindingSeverityLevel = (severity: number): FindingSeverityLevel => {
  if (severity >= 4) return 'high';
  if (severity >= 2) return 'medium';
  return 'low';
};

export const standardDeviationGroupKey = (item: {
  id: string;
  standardDocId: string;
  standardRecordId?: string | null;
  standardChunkIndex: number | null;
}) => `${item.standardDocId}-${item.standardRecordId ?? item.standardChunkIndex ?? item.id}`;

const UNKNOWN_STANDARD_NAME = 'Unknown standard';

const clampProgress = (progress: number | null | undefined) => {
  if (!Number.isFinite(progress)) return 0;
  return Math.max(0, Math.min(100, progress ?? 0));
};

const getRunTimestamp = (item: StandardDeviation) =>
  new Date(item.updatedAt || item.createdAt || 0).getTime();

const getLatestRunByStandardEvalId = (data: StandardDeviation[]) => {
  const latestRunByStandardEvalId = new Map<string, StandardDeviation>();

  for (const item of data) {
    const existing = latestRunByStandardEvalId.get(item.standardEvalId);
    if (!existing || getRunTimestamp(item) >= getRunTimestamp(existing)) {
      latestRunByStandardEvalId.set(item.standardEvalId, item);
    }
  }

  return latestRunByStandardEvalId;
};

const createTemplateStatus = ({
  standardEvalId,
  name,
  indexed,
  run,
  isKnownTemplate,
}: {
  standardEvalId: string;
  name: string;
  indexed: boolean;
  run?: StandardDeviation;
  isKnownTemplate: boolean;
}): StandardDeviationTemplateStatus => {
  const status = run?.status ?? AnalysisStatus.NOT_STARTED;
  const statementCount = run?.statements?.length ?? 0;
  const isRunning = status === AnalysisStatus.IN_PROGRESS;
  const isCompleted = status === AnalysisStatus.DONE || status === AnalysisStatus.NOT_FOUND;
  const isFailed = status === AnalysisStatus.FAILED;

  return {
    standardEvalId,
    name,
    indexed,
    status,
    progress: clampProgress(run?.progress),
    statementCount,
    hasRun: !!run,
    hasViewableResults: isKnownTemplate && statementCount > 0,
    isRunning,
    isCompleted,
    isFailed,
  };
};

export const getStandardDeviationTemplateStatusData = (
  data: StandardDeviation[],
  knowledgeBases: Pick<KnowledgeBase, 'id' | 'name' | 'indexed'>[],
): StandardDeviationTemplateStatusData => {
  const latestRunByStandardEvalId = getLatestRunByStandardEvalId(data ?? []);
  const knowledgeBaseById = new Map(knowledgeBases.map((kb) => [kb.id, kb] as const));

  const templates = knowledgeBases.map((kb) =>
    createTemplateStatus({
      standardEvalId: kb.id,
      name: kb.name,
      indexed: kb.indexed,
      run: latestRunByStandardEvalId.get(kb.id),
      isKnownTemplate: true,
    }),
  );

  for (const run of latestRunByStandardEvalId.values()) {
    if (knowledgeBaseById.has(run.standardEvalId)) continue;
    templates.push(
      createTemplateStatus({
        standardEvalId: run.standardEvalId,
        name: UNKNOWN_STANDARD_NAME,
        indexed: false,
        run,
        isKnownTemplate: false,
      }),
    );
  }

  templates.sort((a, b) => a.name.localeCompare(b.name));

  const byStandardEvalId = new Map(
    templates.map((template) => [template.standardEvalId, template]),
  );
  const runningTemplates = templates.filter((template) => template.isRunning);
  const completedTemplates = templates.filter((template) => template.isCompleted);
  const failedTemplates = templates.filter((template) => template.isFailed);
  const viewableStatementCount = templates.reduce(
    (sum, template) => sum + (template.hasViewableResults ? template.statementCount : 0),
    0,
  );
  const templatesWithRuns = templates.filter((template) => template.hasRun);
  const progressPercentage = templatesWithRuns.length
    ? Math.round(
        templatesWithRuns.reduce((sum, template) => sum + template.progress, 0) /
          templatesWithRuns.length,
      )
    : 0;
  const runningProgressPercentage = runningTemplates.length
    ? Math.round(
        runningTemplates.reduce((sum, template) => sum + template.progress, 0) /
          runningTemplates.length,
      )
    : 0;

  return {
    templates,
    byStandardEvalId,
    completedTemplates,
    failedTemplates,
    hasRunningTemplates: runningTemplates.length > 0,
    hasViewableResults: viewableStatementCount > 0,
    viewableStatementCount,
    progressPercentage,
    runningProgressPercentage,
  };
};

export type StatusCount = {
  status: UserReview;
  count: number;
};

export type GroupedDeviation = {
  groupKey: string;
  groupLabel: string;
  deviations: DeviationsFlatRow[];
  totalCount: number;
  statusCounts: StatusCount[];
  evalDocId: string;
  evalSource: string;
  maxSeverity: number;
  dominantType: StandardDeviationStatementType | null;
};

export const findStandardDeviationGroup = (
  groups: GroupedDeviation[],
  deviationId: string,
  statementId: string | undefined,
) =>
  groups.find((group) => group.groupKey === deviationId) ??
  groups.find((group) => group.deviations.some((deviation) => deviation.item.id === statementId));

export const getGroupDisplayType = (group: GroupedDeviation) => {
  if (group.dominantType) return group.dominantType;
  for (const deviation of group.deviations) {
    if (deviation.item.type) return deviation.item.type;
  }
  return null;
};

export const getActiveStatementIndex = (group: GroupedDeviation, statementId: string | undefined) =>
  Math.max(
    0,
    group.deviations.findIndex((d) => d.item.id === statementId),
  );

export const sumGroupCommentCounts = (
  group: GroupedDeviation,
  countsMap: Map<string, { totalCount: number; unreadCount: number }> | undefined,
) => {
  let totalCount = 0;
  let unreadCount = 0;
  for (const { item } of group.deviations) {
    const counts = countsMap?.get(`${SubjectKind.STANDARD_DEVIATION_STATEMENT}:${item.id}`);
    totalCount += counts?.totalCount ?? 0;
    unreadCount += counts?.unreadCount ?? 0;
  }
  return { totalCount, unreadCount };
};

export const groupDeviations = (rows: DeviationsFlatRow[]): GroupedDeviation[] => {
  const groupMap = new Map<string, DeviationsFlatRow[]>();

  for (const row of rows) {
    const key = standardDeviationGroupKey(row.item);
    if (!groupMap.has(key)) {
      groupMap.set(key, []);
    }
    groupMap.get(key)!.push(row);
  }

  const groups: GroupedDeviation[] = [];

  for (const [key, deviations] of groupMap.entries()) {
    const firstDev = deviations[0];
    if (!firstDev) continue;

    const docName = firstDev.item.standardDocName || '(Deleted standard)';
    const section = firstDev.item.standardChunkIndex ?? 'Unknown';
    const label = `${docName} - Section ${section}`;

    const statusCountMap = new Map<UserReview, number>();
    const typeCountMap = new Map<StandardDeviationStatementType, number>();
    let maxSeverity = 0;

    for (const dev of deviations) {
      const status = normalizeUserReview(dev.item.userReview);
      statusCountMap.set(status, (statusCountMap.get(status) || 0) + 1);
      maxSeverity = Math.max(maxSeverity, dev.item.severity || 0);
      if (dev.item.type) {
        typeCountMap.set(dev.item.type, (typeCountMap.get(dev.item.type) || 0) + 1);
      }
    }

    let dominantType: StandardDeviationStatementType | null = null;
    let maxTypeCount = 0;
    for (const [type, count] of typeCountMap) {
      if (count > maxTypeCount) {
        maxTypeCount = count;
        dominantType = type;
      }
    }

    const statusCounts: StatusCount[] = Array.from(statusCountMap.entries()).map(
      ([status, count]) => ({ status, count }),
    );

    groups.push({
      groupKey: key,
      groupLabel: label,
      deviations,
      totalCount: deviations.length,
      statusCounts,
      evalDocId: firstDev.item.evalDocId,
      evalSource: firstDev.item.evalSource,
      maxSeverity,
      dominantType,
    });
  }

  return groups;
};

const slugify = (name: string) => {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

export const getStandardDeviationsExportFileName = (
  sidebarStandardKey: string | undefined,
  groups: GroupedDeviation[],
) => {
  const base = 'technical-compliance';
  if (!sidebarStandardKey || groups.length === 0) return base;

  const first = groups[0]?.deviations[0];
  const label = first?.standardName;
  const slug = label ? slugify(label) : '';
  return slug ? `${slug}-technical-compliance` : base;
};

const getGroupStatusOrder = (group: GroupedDeviation) =>
  Math.max(0, ...group.deviations.map((d) => getUserReviewSortValue(d.item.userReview)));

export const filterStandardDeviationGroups = (
  rows: DeviationsFlatRow[],
  filter: StandardDeviationsFilter,
  selection: { standardKey?: string; docKey?: string },
  search = '',
) => {
  const sortDir = filter.order.direction === 'asc' ? -1 : 1;
  const query = search.trim().toLowerCase();
  return groupDeviations(
    rows.filter((d) => {
      if (selection.standardKey && d.kbId !== selection.standardKey) return false;
      if (selection.docKey && d.item.evalDocId !== selection.docKey) return false;
      if (!filter.showBad && d.item.isPositiveFeedback === false) return false;
      if (!filter.showNotRelevant && isIgnoredUserReview(d.item.userReview)) return false;
      if (failsModeFilter(filter.status, normalizeUserReview(d.item.userReview), filter.statusMode))
        return false;
      if (d.item.type && failsModeFilter(filter.type, d.item.type, filter.typeMode)) return false;
      return (
        !query ||
        [
          d.statement,
          d.standardName,
          d.item.analysis,
          d.item.severityRationale,
          d.item.evalDocName,
          d.item.standardDocName,
        ].some((field) => field?.toLowerCase().includes(query))
      );
    }),
  ).sort((a, b) => {
    const cmp =
      filter.order.field === 'status'
        ? (getGroupStatusOrder(b) - getGroupStatusOrder(a)) * sortDir
        : (a.maxSeverity - b.maxSeverity) * sortDir;
    return cmp !== 0 ? cmp : a.groupLabel.localeCompare(b.groupLabel);
  });
};
